import { AlertTriangle, ArrowLeft, Check, Eye, EyeOff, Mail, ShieldCheck } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaOtpInput } from "@/shared/components/forms/ColocaOtpInput";
import { ColocaPasswordField } from "@/shared/components/forms/ColocaPasswordField";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import {
  SECURITY_PIN_SETUP_STEPS,
  type SecurityPinSetupController,
  type SecurityPinSetupStep,
} from "./useSecurityPinSetup";

type GlobalT = ReturnType<typeof useTranslations<"global">>["t"];

const ISSUE_KEYS = {
  repeated: "security_pin_issue_repeated",
  sequence: "security_pin_issue_sequence",
  pattern: "security_pin_issue_pattern",
  date: "security_pin_issue_date",
  common: "security_pin_issue_common",
  phone: "security_pin_issue_phone",
} as const;

/**
 * "Step 1 of 3" and three segments that fill as the person moves; the current one grows in so the bar reads
 * as progress, not decoration. Hosts put it in a dialog's toolbar or above the inline form.
 */
export function SecurityPinStepper({ step, className }: { step: SecurityPinSetupStep; className?: string }) {
  const { t } = useTranslations("global");
  const index = step === "done" ? SECURITY_PIN_SETUP_STEPS.length : SECURITY_PIN_SETUP_STEPS.indexOf(step);
  const shown = Math.min(index + 1, SECURITY_PIN_SETUP_STEPS.length);

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div className="flex flex-1 gap-1.5" aria-hidden="true">
        {SECURITY_PIN_SETUP_STEPS.map((name, i) => (
          <span key={name} className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
            <span
              className={cn(
                "block h-full origin-left rounded-full bg-primary transition-transform duration-500 ease-out motion-reduce:transition-none",
                i <= index ? "scale-x-100" : "scale-x-0",
              )}
            />
          </span>
        ))}
      </div>
      <span className="shrink-0 font-mono text-[10.5px] uppercase tracking-[0.06em] text-muted-foreground">
        {t("security_pin_step_label", {
          step: String(shown),
          total: String(SECURITY_PIN_SETUP_STEPS.length),
        })}
      </span>
    </div>
  );
}

export interface SecurityPinInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  invalid: boolean;
  shakeKey: number;
  label: string;
  autoFocus?: boolean;
  disabled?: boolean;
}

/** Six masked slots with a reveal toggle; the row shakes each time `shakeKey` moves. */
export function SecurityPinInput({
  value,
  onChange,
  onComplete,
  invalid,
  shakeKey,
  label,
  autoFocus,
  disabled,
}: SecurityPinInputProps) {
  const { t } = useTranslations("global");
  const [visible, setVisible] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <div
        key={shakeKey}
        className={cn("min-w-0 flex-1", shakeKey > 0 && "animate-shake motion-reduce:animate-none")}
      >
        <ColocaOtpInput
          value={value}
          onValueChange={onChange}
          onComplete={onComplete}
          masked={!visible}
          invalid={invalid}
          autoFocus={autoFocus}
          disabled={disabled}
          aria-label={label}
        />
      </div>
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? t("security_pin_hide") : t("security_pin_show")}
        aria-pressed={visible}
        className="grid size-9 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
      >
        {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
}

function FieldNote({
  tone,
  children,
}: {
  tone: "muted" | "warning" | "destructive" | "success";
  children: string;
}) {
  const Icon = tone === "success" ? Check : tone === "muted" ? null : AlertTriangle;
  return (
    <p
      role={tone === "destructive" || tone === "warning" ? "alert" : undefined}
      className={cn(
        "flex min-h-5 animate-fade animate-duration-200 items-center gap-1.5 text-[12px] motion-reduce:animate-none",
        tone === "muted" && "text-muted-foreground",
        tone === "warning" && "font-medium text-warning",
        tone === "destructive" && "font-medium text-destructive",
        tone === "success" && "font-medium text-success",
      )}
    >
      {Icon ? <Icon className="size-3.5 shrink-0" aria-hidden="true" /> : null}
      {children}
    </p>
  );
}

function createNote(controller: SecurityPinSetupController, t: GlobalT) {
  if (controller.pinError) return <FieldNote tone="destructive">{controller.pinError}</FieldNote>;
  if (controller.pinIssue) return <FieldNote tone="warning">{t(ISSUE_KEYS[controller.pinIssue])}</FieldNote>;
  if (controller.pin.length === 6)
    return <FieldNote tone="success">{t("security_pin_strength_ok")}</FieldNote>;
  return <FieldNote tone="muted">{t("security_pin_step_create_hint")}</FieldNote>;
}

export interface SecurityPinSetupBodyProps {
  controller: SecurityPinSetupController;
  /** What the success state says comes next — e.g. that the pending action continues. */
  doneBody?: string;
  className?: string;
}

/**
 * The step on screen. Each step is keyed, so moving forward or back replays its entrance from the side it
 * comes from; reduced motion shows it in place.
 */
export function SecurityPinSetupBody({ controller, doneBody, className }: SecurityPinSetupBodyProps) {
  const { t } = useTranslations("global");
  const { step, passwordForm } = controller;

  // The password step opens ready to type, as the two PIN steps do.
  useEffect(() => {
    if (step === "password") passwordForm.setFocus("password");
  }, [step, passwordForm]);

  const entrance =
    controller.direction === "forward"
      ? "animate-fade-left animate-duration-300 motion-reduce:animate-none"
      : "animate-fade-right animate-duration-300 motion-reduce:animate-none";

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {controller.formError ? (
        <ColocaAlert variant="destructive" icon={AlertTriangle} text={controller.formError} />
      ) : null}

      <div key={step} className={cn("flex flex-col gap-3", entrance)}>
        {step === "create" && (
          <>
            <StepHeading title={t("security_pin_step_create_title")} />
            <SecurityPinInput
              value={controller.pin}
              onChange={controller.setPin}
              invalid={!!controller.pinIssue || !!controller.pinError}
              shakeKey={controller.shakeKey}
              label={t("security_pin_step_create_title")}
              autoFocus
            />
            {createNote(controller, t)}
          </>
        )}

        {step === "confirm" && (
          <>
            <StepHeading title={t("security_pin_step_confirm_title")} />
            <SecurityPinInput
              value={controller.confirm}
              onChange={controller.setConfirm}
              invalid={!!controller.confirmError}
              shakeKey={controller.shakeKey}
              label={t("security_pin_step_confirm_title")}
              autoFocus
            />
            {controller.confirmError ? (
              <FieldNote tone="destructive">{controller.confirmError}</FieldNote>
            ) : (
              <FieldNote tone="muted">{t("security_pin_step_confirm_hint")}</FieldNote>
            )}
          </>
        )}

        {step === "password" && (
          <>
            <StepHeading
              title={t("security_pin_step_password_title")}
              sub={t("security_pin_step_password_hint")}
            />
            <ColocaForm
              form={controller.passwordForm}
              onSubmit={controller.submitPassword}
              id={controller.passwordFormId}
              className="space-y-0"
            >
              <div
                key={controller.shakeKey}
                className={cn(controller.shakeKey > 0 && "animate-shake motion-reduce:animate-none")}
              >
                <ColocaPasswordField
                  name="password"
                  label={t("security_pin_password_label")}
                  autoComplete="current-password"
                  showLabel={t("security_pin_password_show")}
                  hideLabel={t("security_pin_password_hide")}
                />
              </div>
            </ColocaForm>
          </>
        )}

        {step === "done" && <SecurityPinDone body={doneBody ?? t("security_pin_done_body")} />}
      </div>
    </div>
  );
}

function StepHeading({ title, sub }: { title: string; sub?: string }) {
  return (
    <div>
      <div className="text-[14px] font-semibold text-foreground">{title}</div>
      {sub ? <div className="mt-0.5 text-[12.5px] text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

/**
 * The landing: a shield that pops in with one soft pulse, then the sentence about what comes next — and, when
 * the server turned it on with the PIN, that the email backup now covers a forgotten one.
 */
export function SecurityPinDone({ body }: { body: string }) {
  const { t } = useTranslations("global");
  const { user } = useAuth();
  return (
    <div className="flex flex-col items-center gap-3 py-2 text-center" aria-live="polite">
      <span className="relative grid size-14 place-items-center">
        <span
          aria-hidden="true"
          className="absolute inset-0 animate-ping rounded-full bg-success/25 animate-once animate-duration-1000 motion-reduce:hidden"
        />
        <span className="relative grid size-14 animate-jump-in place-items-center rounded-full bg-success/15 text-success animate-duration-500 motion-reduce:animate-none">
          <ShieldCheck className="size-7" aria-hidden="true" />
        </span>
      </span>
      <div className="animate-fade-up animate-delay-150 animate-duration-300 motion-reduce:animate-none">
        <div className="text-[15px] font-semibold text-foreground">{t("security_pin_done_title")}</div>
        <div className="mt-1 text-[12.5px] text-muted-foreground">{body}</div>
      </div>
      {user?.step_up_email_enabled ? (
        <div className="flex animate-fade-up items-start gap-1.5 rounded-md border border-primary/25 bg-primary/5 px-2.5 py-1.5 text-left text-[12px] text-foreground animate-delay-300 animate-duration-300 motion-reduce:animate-none">
          <Mail size={13} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
          {t("security_pin_done_email_backup")}
        </div>
      ) : null}
    </div>
  );
}

export interface SecurityPinSetupActionsProps {
  controller: SecurityPinSetupController;
  /** Label of the action that saves the PIN — say what happens: "Create PIN and continue". */
  submitLabel: string;
  /** The main action once the PIN is saved, and what it does. Omit to render no action on `done`. */
  done?: { label: string; onClick: () => void };
  /** Shown instead of "Back" on the first step (e.g. the host's way out). */
  leading?: ReactNode;
  className?: string;
}

/**
 * The flow's action group: the way back on the left, the one primary action last. The primary always says
 * what it does next — continue, save the PIN, or what the host continues with.
 */
export function SecurityPinSetupActions({
  controller,
  submitLabel,
  done,
  leading,
  className,
}: SecurityPinSetupActionsProps) {
  const { t } = useTranslations("global");
  const { step } = controller;

  const backAction =
    step === "confirm" || step === "password" ? (
      <ColocaButton variant="ghost" icon={ArrowLeft} onClick={controller.back} disabled={controller.saving}>
        {t("security_pin_back")}
      </ColocaButton>
    ) : step === "create" ? (
      leading
    ) : null;

  let primary: ReactNode = null;
  if (step === "create" || step === "confirm") {
    primary = (
      <ColocaButton variant="primary" onClick={controller.next} disabled={!controller.canContinue}>
        {t("security_pin_next")}
      </ColocaButton>
    );
  } else if (step === "password") {
    primary = (
      <ColocaButton
        variant="primary"
        type="submit"
        form={controller.passwordFormId}
        loading={controller.saving}
      >
        {submitLabel}
      </ColocaButton>
    );
  } else if (done) {
    primary = (
      <ColocaButton
        variant="primary"
        onClick={done.onClick}
        className="animate-fade animate-delay-300 motion-reduce:animate-none"
      >
        {done.label}
      </ColocaButton>
    );
  }

  return (
    <div
      className={cn(
        "flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end",
        className,
      )}
    >
      {backAction ? <div className="sm:mr-auto [&>*]:w-full sm:[&>*]:w-auto">{backAction}</div> : null}
      {primary ? <div className="[&>*]:w-full sm:[&>*]:w-auto">{primary}</div> : null}
    </div>
  );
}
